import * as React from "react";
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import BrokenImageOutlinedIcon from "@mui/icons-material/BrokenImageOutlined";
import AddItemModal from "./AddItemModal";
import { Close } from "@mui/icons-material";
import { ErrorAlert, SuccessAlert } from "../../utils/Alerts";
import UpdateItemModal from "./UpdateItemModal";
import ViewItemModal from "./ViewItemModal";
import {
  useDeleteItemMutation,
  useGetAllItemsQuery,
  useGetPartnerZonesQuery,
} from "../../Slices/roomNateApis";
import { useState } from "react";
import { baseUrl } from "../../utils/baseUrl";
import { useGlobalAuthContext } from "../../context/AuthContext";
import GroupedGrid from "../GlobalStyles/GroupedGrid";
import { useGlobalContext } from "../../context/GlobalContext";

// ── Sort options ──────────────────────────────────────────────────────────────

const SORT_OPTIONS = [
  { value: "newest", label: "Recently Added" },
  { value: "oldest", label: "Oldest First" },
  { value: "name-asc", label: "Name: A → Z" },
  { value: "name-desc", label: "Name: Z → A" },
];

function sortItems(items, sortBy) {
  if (!items) return [];
  const arr = [...items];
  switch (sortBy) {
    case "name-asc":
      return arr.sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
    case "name-desc":
      return arr.sort((a, b) => (b.name ?? "").localeCompare(a.name ?? ""));
    case "oldest":
      // MongoDB ObjectId first 8 hex chars encode a Unix timestamp
      return arr.sort((a, b) => (a._id ?? "").localeCompare(b._id ?? ""));
    case "newest":
    default:
      return arr.sort((a, b) => (b._id ?? "").localeCompare(a._id ?? ""));
  }
}

// ── Item card ─────────────────────────────────────────────────────────────────

function ImagePlaceholder() {
  return (
    <Box
      sx={{
        width: "100%",
        height: 160,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "rgba(0,0,0,0.04)",
        color: "rgba(0,0,0,0.18)",
      }}
    >
      <BrokenImageOutlinedIcon />
    </Box>
  );
}

function ItemCard({
  row,
  zonesData,
  shapesData,
  connectionPointsData,
  connectionPointsOptionsData,
  allItems,
  open,
  selectedId,
  handleClickOpen,
  handleClose,
  handleDelete,
}) {
  return (
    <Box
      sx={{
        borderRadius: "14px",
        border: "1px solid rgba(0,0,0,0.08)",
        overflow: "hidden",
        bgcolor: "white",
        boxShadow: "0 1px 4px rgba(0,0,0,0.06), 0 0 1px rgba(0,0,0,0.04)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* ── Front view image ── */}
      {row?.frontViewImage ? (
        <img
          src={`${baseUrl}/g${row.frontViewImage}`}
          alt={row.name}
          style={{
            width: "100%",
            height: 160,
            objectFit: "contain",
            display: "block",
            backgroundColor: "#f7f7f7",
          }}
        />
      ) : (
        <ImagePlaceholder />
      )}

      {/* ── Name ── */}
      <Box sx={{ px: 1.5, py: 1, flexGrow: 1 }}>
        <Typography
          sx={{
            fontSize: "0.875rem",
            fontWeight: 500,
            letterSpacing: "-0.01em",
            color: "text.primary",
            lineHeight: 1.3,
          }}
        >
          {row.name}
        </Typography>
      </Box>

      {/* ── Actions: Preview → Edit → Delete ── */}
      <Divider />
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          px: 1,
          py: 0.5,
          gap: 0.25,
        }}
      >
        <ViewItemModal
          items={row}
          connectionPointsOptionsData={connectionPointsOptionsData}
          zonesData={zonesData}
        />

        <UpdateItemModal
          item={allItems}
          items={row}
          zonesData={zonesData}
          shapesData={shapesData}
          connectionPointsData={connectionPointsData}
          connectionPointsOptionsData={connectionPointsOptionsData}
        />

        <Tooltip title="Delete">
          <IconButton
            size="small"
            color="primary"
            onClick={() => handleClickOpen(row._id)}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        {open && selectedId === row._id && (
          <Dialog
            maxWidth="sm"
            fullWidth
            open={open}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
          >
            <DialogTitle id="alert-dialog-title">
              {"Confirm the Action?"}
            </DialogTitle>
            <Box position="absolute" top={10} right={0}>
              <IconButton onClick={handleClose}>
                <Close />
              </IconButton>
            </Box>
            <DialogContent>
              <DialogContentText id="alert-dialog-description">
                Do you really want to delete the item?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose} variant="outlined">
                Cancel
              </Button>
              <Button onClick={handleDelete} variant="contained" autoFocus>
                Confirm
              </Button>
            </DialogActions>
          </Dialog>
        )}
      </Box>
    </Box>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────

export default function BasicTable({
  zonesData,
  connectionPointsOptionsData,
  connectionPointsData,
  shapesData,
  gridView,
}) {
  const { currentUserSessionStorage } = useGlobalAuthContext();
  const { activePartner } = useGlobalContext();

  const [selectedId, setSelectedId] = React.useState();
  const [open, setOpen] = React.useState(false);
  const [sortBy, setSortBy] = useState("newest");

  const {
    data: FetchItems,
    refetch,
    isFetching,
  } = useGetAllItemsQuery({ itemType: 1, activePartner });

  // When a partner is active, use their zone definitions for the filter
  // dropdown so the zone values match what's stored on their items.
  const { data: partnerZones, error: partnerZonesError } = useGetPartnerZonesQuery(
    activePartner,
    { skip: !activePartner }
  );
  const effectiveZonesData =
    activePartner && !partnerZonesError && partnerZones?.length > 0
      ? partnerZones.filter((z) => z?.value !== "custom")
      : zonesData;

  const [deleteItem, resultStats] = useDeleteItemMutation();
  const [successAlert, setSuccessAlert] = React.useState(false);
  const [errorAlert, setErrorAlert] = React.useState(false);

  const [selectedZone, setSelectedZone] = useState(null);
  const [selectedShape] = useState(null);

  const filteredData = React.useMemo(() => {
    const filtered = FetchItems?.items?.filter((item) => {
      if (!selectedZone && !selectedShape) return true;
      if (selectedZone && item.zones.map((z) => z.toLowerCase()).includes(selectedZone.toLowerCase())) return true;
      if (selectedShape && item.shapes.map((s) => s.toLowerCase()).includes(selectedShape.toLowerCase())) return true;
      return false;
    });
    return sortItems(filtered, sortBy);
  }, [FetchItems?.items, selectedZone, selectedShape, sortBy]);

  const groupedData = React.useMemo(
    () =>
      filteredData?.reduce((acc, item) => {
        item.zones.forEach((zone) => {
          if (!acc[zone]) acc[zone] = [];
          acc[zone].push(item);
        });
        return acc;
      }, {}),
    [filteredData]
  );

  const handleClickOpen = (id) => {
    setOpen(true);
    setSelectedId(id);
  };

  const handleClose = () => setOpen(false);

  const handleDelete = () => {
    handleClose();
    deleteItem(selectedId);
  };

  React.useEffect(() => {
    if (resultStats?.isSuccess) {
      setSuccessAlert(true);
      setTimeout(() => setSuccessAlert(false), 2000);
    }
    if (resultStats?.isError) {
      setErrorAlert(true);
      setTimeout(() => setErrorAlert(false), 2000);
    }
  }, [resultStats]);

  React.useEffect(() => {
    refetch();
  }, [currentUserSessionStorage, refetch]);

  const cardProps = {
    zonesData,
    shapesData,
    connectionPointsData,
    connectionPointsOptionsData,
    allItems: FetchItems?.items,
    open,
    selectedId,
    handleClickOpen,
    handleClose,
    handleDelete,
  };

  return (
    <>
      {successAlert && <SuccessAlert title="Item has been deleted successfully!" />}
      {errorAlert && <ErrorAlert title="Something Went Wrong!" />}

      {gridView ? (
        <Box>
          {isFetching ? (
            <Typography>Loading...</Typography>
          ) : (
            <GroupedGrid groupedData={groupedData} />
          )}
        </Box>
      ) : (
        <Box>
          {/* Filters row */}
          <Box sx={{ mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              {effectiveZonesData && (
                <Grid item xs={12} sm={6} md={4}>
                  <Autocomplete
                    disablePortal
                    id="item-zone-filter"
                    options={effectiveZonesData}
                    fullWidth
                    onChange={(_, value) =>
                      setSelectedZone((value || value === "") && value.value)
                    }
                    renderInput={(params) => (
                      <TextField {...params} label="Filter by Zone" size="small" />
                    )}
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={6} md={3}>
                <FormControl size="small" fullWidth>
                  <InputLabel id="item-sort-label">Sort by</InputLabel>
                  <Select
                    labelId="item-sort-label"
                    value={sortBy}
                    label="Sort by"
                    onChange={(e) => setSortBy(e.target.value)}
                    sx={{ borderRadius: "10px" }}
                  >
                    {SORT_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>

          {/* Count + Add button */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: "0.8125rem" }}
            >
              {isFetching ? "Loading…" : `${filteredData?.length ?? 0} items`}
            </Typography>

            <AddItemModal
              items={FetchItems?.items}
              zonesData={zonesData}
              shapesData={shapesData}
              connectionPointsData={connectionPointsData}
              connectionPointsOptionsData={connectionPointsOptionsData}
            />
          </Box>

          {/* Card grid */}
          {!isFetching && filteredData?.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
              No items found.
            </Typography>
          ) : (
            <Grid container spacing={2}>
              {filteredData?.map((row, index) => (
                <Grid item xs={12} sm={6} lg={4} key={row._id ?? index}>
                  <ItemCard row={row} {...cardProps} />
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      )}
    </>
  );
}
